/* eslint-disable max-len */
import * as Sentry from '@sentry/react'
import jwtDecode from 'jwt-decode'
import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { useLazyQuery, useQuery } from 'react-apollo'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { Button } from '../../components'
import IconNetwork from '../../components/icons/IconNetwork/IconNetwork'
import useNetwork from '../../hooks/useNetwork'

import {
  ADDRESSES,
  CITIES,
  GET_CATEGORIES,
  GET_CLIENTS,
  GET_DELIVERIES,
  GET_FAV_MATERIALS,
  GET_FORWARDERS,
  GET_USER,
  GET_ORDER_DELIVERIES,
} from '../../gql'
import { db } from '../../lib/db'
import Check from './Check.png'
import Fail from './Fail.png'

const Loader = () => {
  const { t } = useTranslation('home')
  const history = useHistory()

  const [clientFetch, setClientFetch] = useState(0)
  const [orderDeliveriesFetch, setOrderDeliveriesFetch] = useState(0)
  const [salesFetch, setSalesFetch] = useState(0)
  const [deliveryFetch, setDeliveryFetch] = useState(0)
  const [userFetch, setUserFetch] = useState(0)
  const [catFetch, setCatFetch] = useState(0)
  const [favFetch, setFavFetch] = useState(0)
  const [forwarderFetch, setForwarderFetch] = useState(0)
  const [addressesFetch, setAddressesFetch] = useState(0)
  const [citiesFetch, setCitiesFetch] = useState(0)

  const [clientCount, setClientCount] = useState(0)
  const [orderDeliveriesCount, setOrderDeliveriesCount] = useState(0)
  const [deliveryCount, setDeliveryCount] = useState(0)
  const [salesCount, setSalesCount] = useState(0)
  const [catCount, setCatCount] = useState(0)
  const [favCount, setFavCount] = useState(0)
  const [forwarderCount, setForwarderCount] = useState(0)
  const [userCount, setUserCount] = useState(1)
  const network = useNetwork()

  const [clientError, setClientError] = useState(false)
  const [orderDeliveriesError, setOrderDeliveriesError] = useState(false)
  const [salesError, setSalesError] = useState(false)
  const [deliveryError, setDeliveryError] = useState(false)
  const [userError, setUserError] = useState(false)
  const [catError, setCatError] = useState(false)
  const [favError, setFavError] = useState(0)
  const [forwarderError, setForwarderError] = useState(false)

  const [clientFetched, setClientFetched] = useState(false)
  const [orderDeliveriesFetched, setOrderDeliveriesFetched] = useState(false)
  const [salesFetched, setSalesFetched] = useState(false)
  const [deliveryFetched, setDeliveryFetched] = useState(false)
  const [userFetched, setUserFetched] = useState(false)
  const [rubbleFetched, setRubbleFetched] = useState(false)
  const [materialFetched, setMaterialFetched] = useState(false)
  const [favFetched, setFavFetched] = useState(false)
  const [forwarderFetched, setForwarderFetched] = useState(false)

  const [addressesFetched, setAddressesFetched] = useState(false)
  const [addressesError, setAddressesError] = useState(false)
  const [addressesCount, setAddressesCount] = useState(0)
  const [citiesFetched, setCitiesFetched] = useState(false)
  const [citiesError, setCitiesError] = useState(false)
  const [citiesCount, setCitiesCount] = useState(0)

  const { data: userData } = useQuery(GET_USER, {
    onCompleted: () => {
      setUserFetched(true)
      setUserFetch(1)
      setUserCount(1)
    },
    onError: (error) => {
      setUserError(true)
      Sentry.captureException(error)
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: {
      id: localStorage.getItem('access_token')
        ? jwtDecode(localStorage.getItem('access_token')).id
        : '',
    },
  })

  const addSales = (sales, clt) => {
    db.sales
      .put({
        id: sales.id,
        code: sales.code,
        externalReference: sales.externalReference,
        siteByIdSite: {
          id: sales.siteByIdSite.id,
          name: sales.siteByIdSite.name,
        },
        materials: [
          ...sales.salesOrderItemsByIdSalesOrder.nodes.map((mat) => ({
            quantity: mat.quantity,
            matId: mat.materialByIdMaterial.id,
            description: mat.materialByIdMaterial.description,
            family: mat.materialByIdMaterial.productCategoryByIdProductCategory.family,
          })),
        ],
        clientId: clt.node.id,
      })
      .then(
        () => {
          setSalesFetch((prev) => prev + 1)
        },
        () => {
          setSalesFetch((prev) => prev + 1)
        }
      )
  }

  const addClient = (clt) => {
    db.clients
      .put(
        {
          id: clt.node.id,
          active: clt.node.active,
          displayName: clt.node.displayName,
          internalId: clt.node.internalId,
        },
        clt.node.id
      )
      .then(
        () => {
          setClientFetch((prev) => prev + 1)
        },
        () => {
          setClientFetch((prev) => prev + 1)
        }
      )
  }

  const [fetchClients] = useLazyQuery(GET_CLIENTS, {
    onCompleted: async (clients) => {
      db.clients.clear().finally(() => {
        setClientFetched(true)
        setSalesFetched(true)
        setClientCount((prev) => prev + clients?.clients.edges.length)
        setSalesCount(
          (prev) =>
            prev +
            clients?.clients.edges.reduce(
              (acc, clt) => acc + clt.node.salesOrdersByIdClient.nodes.length,
              0
            )
        )
        clients?.clients.edges.forEach((clt) => {
          addClient(clt)
          clt.node.salesOrdersByIdClient.nodes.forEach((sales) => {
            db.sales.delete(sales.id).then(
              () => {
                addSales(sales, clt)
              },
              () => {
                addSales(sales, clt)
              }
            )
          })
        })
      })
    },
    onError: (error) => {
      setSalesError(true)
      setClientError(true)
      Sentry.captureException(error)
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      idSite: userData?.user?.idSite,
      greaterThan: moment().subtract(3, 'months').format('YYYY-MM-DD'),
    },
  })

  const addRubble = (material) => {
    db.rubble.put({ id: material.id, description: material.description }, material.id).then(
      () => {
        setCatFetch((prev) => prev + 1)
      },
      () => {
        setCatFetch((prev) => prev + 1)
      }
    )
  }

  useQuery(GET_CATEGORIES, {
    skip: !userData?.user?.idSite,
    onCompleted: (cats) => {
      setRubbleFetched(true)
      cats?.productCategories.nodes.forEach((cat) => {
        cat.materialsByIdProductCategory.nodes.forEach((materialsByIdProductCategory) => {
          setCatCount(
            (prev) => prev + materialsByIdProductCategory.siteMaterialsByIdMaterial.nodes.length
          )
          materialsByIdProductCategory.siteMaterialsByIdMaterial.nodes.forEach(
            (materialByIdMaterial) => {
              const material = materialByIdMaterial.materialByIdMaterial
              db.rubble.delete(material.id).then(
                () => {
                  addRubble(material)
                },
                () => {
                  addRubble(material)
                }
              )
            }
          )
        })
      })
    },
    onError: (error) => {
      setCatError(true)
      Sentry.captureException(error)
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      idSite: userData?.user?.idSite,
      family: 'RUBBLE',
    },
  })

  const addForwarder = (fw) => {
    db.forwarders.put({ id: fw.id, displayName: fw.displayName }, fw.id).then(
      () => {
        setForwarderFetch((prev) => prev + 1)
      },
      () => {
        setForwarderFetch((prev) => prev + 1)
      }
    )
  }

  useQuery(GET_FORWARDERS, {
    onCompleted: (forwarders) => {
      db.forwarders.clear().finally(() => {
        setForwarderFetched(true)
        setForwarderCount((prev) => prev + forwarders?.freightForwarders.nodes.length)
        forwarders?.freightForwarders.nodes.forEach((fw) => {
          db.forwarders.delete(fw.id).then(
            () => {
              addForwarder(fw)
            },
            () => {
              addForwarder(fw)
            }
          )
        })
      })
    },
    onError: (error) => {
      setForwarderError(true)
      Sentry.captureException(error)
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  })

  const addMaterial = (material) => {
    db.material.put({ id: material.id, description: material.description }, material.id).then(
      () => {
        setCatFetch((prev) => prev + 1)
      },
      () => {
        setCatFetch((prev) => prev + 1)
      }
    )
  }

  useQuery(GET_CATEGORIES, {
    skip: !userData?.user?.idSite,
    onCompleted: (cats) => {
      setMaterialFetched(true)
      cats?.productCategories.nodes.forEach((cat) => {
        cat.materialsByIdProductCategory.nodes.forEach((materialsByIdProductCategory) => {
          setCatCount(
            (prev) => prev + materialsByIdProductCategory.siteMaterialsByIdMaterial.nodes.length
          )
          materialsByIdProductCategory.siteMaterialsByIdMaterial.nodes.forEach(
            (materialByIdMaterial) => {
              const material = materialByIdMaterial.materialByIdMaterial
              db.material.delete(material.id).then(
                () => {
                  addMaterial(material)
                },
                () => {
                  addMaterial(material)
                }
              )
            }
          )
        })
      })
    },
    onError: (error) => {
      setCatError(true)
      Sentry.captureException(error)
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      idSite: userData?.user?.idSite,
      family: 'MATERIAL',
    },
  })

  const [fetchFavorites] = useLazyQuery(GET_FAV_MATERIALS, {
    onCompleted: (favorites) => {
      favorites?.favMaterials?.nodes.forEach((favorite) =>
        addFavorite(favorite?.materialByIdMaterial)
      )
      setFavFetched(true)
      setFavCount(favorites?.favMaterials?.nodes?.length)
    },
    onError: (error) => {
      setFavError(true)
      Sentry.captureException(error)
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      id: userData?.user?.id,
    },
  })

  const addFavorite = (favorite) => {
    db.favorites.put({ id: favorite.id }, favorite.id).then(
      () => {
        setFavFetch((prev) => prev + 1)
      },
      () => {
        setFavFetch((prev) => prev + 1)
      }
    )
  }

  const addDelivery = (delivery) => {
    const bound = delivery
    const products = delivery.draftOutboundDeliveryItemsByIdDraftOutboundDelivery?.nodes
    delete bound.draftOutboundDeliveryItemsByIdDraftOutboundDelivery
    db.deliveries.put({ ...bound, products }, delivery.id).then(
      () => {
        setDeliveryFetch((prev) => prev + 1)
      },
      () => {
        setDeliveryFetch((prev) => prev + 1)
      }
    )
  }

  const [fetchDeliveries] = useLazyQuery(GET_DELIVERIES, {
    onCompleted: (deliveries) => {
      db.deliveries.clear().finally(() => {
        deliveries?.draftOutboundDeliveries?.nodes.forEach((delivery) => addDelivery(delivery))
        setDeliveryFetched(true)
        setDeliveryCount(deliveries?.draftOutboundDeliveries?.nodes?.length)
      })
    },
    onError: (error) => {
      setDeliveryError(true)
      Sentry.captureException(error)
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      site: userData?.user?.idSite,
      startDate: moment().subtract(1, 'day').format('yyyy-MM-DD'),
      endDate: moment().add(1, 'day').format('yyyy-MM-DD'),
    },
  })

  const addOrderDelivery = (ol) => {
    const materials = ol?.node?.orderDeliveryMaterials?.nodes?.map((mat) => ({
      quantity: mat?.quantity,
      matId: mat?.material?.id,
      description: mat?.material?.description,
      family: mat?.material?.productCategoryByIdProductCategory?.family,
    }))

    return db.orderDeliveries
      .put(
        {
          id: ol?.node.id,
          orderDeliveryNumber: ol?.node?.orderDeliveryNumber,
          dateDelivery: ol?.node?.dateDelivery,
          clientName: ol?.node?.command?.clientName,
          clientId: ol?.node?.command.clientId,
          constructionSiteAddress: ol?.node?.command?.constructionSite?.address,
          constructionSiteCity: ol?.node?.command?.constructionSite?.city,
          constructionSiteCode: ol?.node?.command?.constructionSite?.code,
          constructionSiteId: ol?.node?.command?.constructionSiteId,
          orderDeliveryMaterials: ol?.node?.orderDeliveryMaterials,
          site: userData.user?.siteByIdSite?.name,
          siteId: userData.user?.idSite,
          materials: materials,
        },
        ol.node.id
      )
      .then(() => {
        setOrderDeliveriesFetch((prev) => prev + 1)
      })
      .catch(() => {
        setOrderDeliveriesFetch((prev) => prev + 1)
      })
  }

  const [fetchOrderDeliveries] = useLazyQuery(GET_ORDER_DELIVERIES, {
    onCompleted: async (orderDeliveries) => {
      db.orderDeliveries.clear().finally(async () => {
        const siteCodeUser = userData?.user?.siteByIdSite?.code
        if (!orderDeliveries?.orderDeliveries?.edges?.length) {
          setOrderDeliveriesFetched(true)
          setOrderDeliveriesCount(0)
          return
        }

        const deliveryPromises = orderDeliveries.orderDeliveries.edges.map(async (ol) => {
          try {
            const deliverySiteCode = ol.node.deliverySiteBdn?.code
            const startingSiteCode = ol.node.startingSiteBdn?.code
            const unloadingSiteCode = ol.node.unloadingSiteBdn?.code
            const status = ol.node.status

            const isSiteCodeValid =
              siteCodeUser &&
              (siteCodeUser === deliverySiteCode ||
                siteCodeUser === startingSiteCode ||
                siteCodeUser === unloadingSiteCode)
            const isStatusValid = status !== 'valid' && status !== 'canceled'

            if (isSiteCodeValid && isStatusValid) {
              return addOrderDelivery(ol)
            }
          } catch (error) {
            console.error('Error processing order delivery:', error)
          }
        })

        await Promise.all(deliveryPromises)

        const totalCount = await db.orderDeliveries.toArray().then((array) => array.length)
        setOrderDeliveriesFetched(true)
        setOrderDeliveriesCount(totalCount)
      })
    },
    onError: (error) => {
      setOrderDeliveriesError(true)
      Sentry.captureException(error)
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      startDate: moment().subtract(1, 'day').format('YYYY-MM-DD'),
      endDate: moment().add(1, 'day').format('YYYY-MM-DD'),
    },
  })

  const chunkArray = (arr, size) => {
    const chunks = []
    for (let i = 0; i < arr.length; i += size) chunks.push(arr.slice(i, i + size))
    return chunks
  }

  const [fetchAddresses] = useLazyQuery(ADDRESSES, {
    onCompleted: async (addressesData) => {
      try {
        const edges = addressesData?.addressAutoFills?.edges ?? []
        const addresses = edges.map(({ node }) => ({ id: node.id, address: node.address }))

        setAddressesCount(addresses.length)

        if (addresses.length === 0) {
          setAddressesFetched(true)
          return
        }

        await db.addresses.clear()

        const chunks = chunkArray(addresses, 1000)
        for (const chunk of chunks) {
          await db.addresses.bulkPut(chunk)
          setAddressesFetch((prev) => prev + chunk.length)
        }

        setAddressesFetched(true)
      } catch (error) {
        console.error(
          'Erreur sync adresses:',
          error,
          'count:',
          addressesData?.addressAutoFills?.edges?.length
        )
        setAddressesError(true)
      }
    },
    onError: (error) => {
      setAddressesError(true)
      Sentry.captureException(error)
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  })

  const [fetchCities] = useLazyQuery(CITIES, {
    onCompleted: async (citiesData) => {
      try {
        const edges = citiesData?.cityAutoFills?.edges ?? []
        const cities = edges.map(({ node }) => ({ id: node.id, city: node.city }))

        setCitiesCount(cities.length)

        if (cities.length === 0) {
          setCitiesFetched(true)
          return
        }

        await db.cities.clear()

        const chunks = chunkArray(cities, 1000)
        for (const chunk of chunks) {
          await db.cities.bulkPut(chunk)
          setCitiesFetch((prev) => prev + chunk.length)
        }

        setCitiesFetched(true)
      } catch (error) {
        console.error(
          'Erreur sync villes:',
          error,
          'count:',
          citiesData?.cityAutoFills?.edges?.length
        )
        setCitiesError(true)
      }
    },
    onError: (error) => {
      setCitiesError(true)
      Sentry.captureException(error)
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  })

  const syncStarted = useRef(false)

  useEffect(() => {
    if (userData) {
      if (syncStarted.current) return
      syncStarted.current = true
      setClientFetch(0)
      setClientCount(0)
      setClientFetched(false)
      setClientError(false)
      setOrderDeliveriesFetch(0)
      setOrderDeliveriesCount(0)
      setOrderDeliveriesFetched(false)
      setOrderDeliveriesError(false)
      setSalesFetch(0)
      setSalesCount(0)
      setSalesFetched(false)
      setSalesError(false)
      setDeliveryFetch(0)
      setDeliveryCount(0)
      setDeliveryFetched(false)
      setDeliveryError(false)
      setFavFetch(0)
      setFavCount(0)
      setFavFetched(false)
      setFavError(0)

      fetchDeliveries()
      fetchClients()
      fetchFavorites()
      fetchOrderDeliveries()

      db.addresses.count().then((count) => {
        if (count > 0) {
          setAddressesFetched(true)
          setAddressesCount(count)
          setAddressesFetch(count)
        } else {
          fetchAddresses()
        }
      })

      db.cities.count().then((count) => {
        if (count > 0) {
          setCitiesFetched(true)
          setCitiesCount(count)
          setCitiesFetch(count)
        } else {
          fetchCities()
        }
      })
    }
  }, [userData])

  useEffect(() => {
    if (
      clientFetch === clientCount &&
      clientFetched &&
      orderDeliveriesFetch === orderDeliveriesCount &&
      orderDeliveriesFetched &&
      salesFetch === salesCount &&
      salesFetched &&
      deliveryFetch === deliveryCount &&
      deliveryFetched &&
      userFetch === userCount &&
      userFetched &&
      catFetch === catCount &&
      rubbleFetched &&
      materialFetched &&
      favFetch === favCount &&
      favFetched &&
      forwarderFetch === forwarderCount &&
      forwarderFetched &&
      addressesFetch === addressesCount &&
      addressesFetched &&
      citiesFetch === citiesCount &&
      citiesFetched
    ) {
      db.lastSync.put({ id: 1, date: moment().format('DD/MM/YY HH:mm') }, 1)
      history.push('/endPending')
    }
  }, [
    clientFetch,
    clientCount,
    clientFetched,
    orderDeliveriesFetch,
    orderDeliveriesCount,
    orderDeliveriesFetched,
    salesFetch,
    salesCount,
    salesFetched,
    deliveryFetch,
    deliveryCount,
    deliveryFetched,
    userFetch,
    userCount,
    userFetched,
    catFetch,
    catCount,
    rubbleFetched,
    materialFetched,
    favFetch,
    favCount,
    favFetched,
    forwarderFetch,
    forwarderCount,
    forwarderFetched,
    addressesFetch,
    addressesCount,
    addressesFetched,
    citiesFetch,
    citiesCount,
    citiesFetched,
  ])

  const styles = {
    container: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start' },
    network: { display: 'flex', justifyContent: 'end', width: '100%', padding: '20px 50px' },
    infos: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginLeft: '20%',
      marginBottom: '20px',
    },
    infosError: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginLeft: '20%',
      marginBottom: '20px',
      color: '#F00',
    },
    image: { height: '30px', marginRight: '15px' },
    ring: { fontWeight: 'bolder' },
    error: {
      color: '#F00',
      fontSize: '18px',
      fontWeight: 'bold',
      textAlign: 'center',
      width: '100%',
      marginTop: '20px',
    },
  }

  const pictoLoading = (fetch, error) => {
    if (error) {
      return <img style={styles.image} src={Fail} alt="Terminer" />
    }

    return fetch >= 1 ? (
      <img style={styles.image} src={Check} alt="Terminer" />
    ) : (
      <div className="ring small" style={styles.ring} />
    )
  }

  const gotoHomePage = () => {
    history.push('/endPending')
  }

  return (
    <div style={styles.container}>
      <div style={styles.network}>
        <IconNetwork active={network} />
      </div>
      <div style={userError ? styles.infosError : styles.infos}>
        {pictoLoading(userFetch === userCount && userFetched, userError)}
        {t('user')}
        {t('loadingCount', { count: userFetch, total: userCount })}
      </div>
      <div style={catError ? styles.infosError : styles.infos}>
        {pictoLoading(catFetch === catCount && rubbleFetched && materialFetched, catError)}
        {t('cat')}
        {t('loadingCount', { count: catFetch, total: catCount })}
      </div>
      <div style={favError ? styles.infosError : styles.infos}>
        {pictoLoading(favFetch === favCount && clientFetched, favError)}
        {t('fav')}
        {t('loadingCount', { count: favFetch, total: favCount })}
      </div>
      <div style={deliveryError ? styles.infosError : styles.infos}>
        {pictoLoading(deliveryFetch === deliveryCount && deliveryFetched, deliveryError)}
        {t('delivery')}
        {t('loadingCount', { count: deliveryFetch, total: deliveryCount })}
      </div>
      <div style={clientError ? styles.infosError : styles.infos}>
        {pictoLoading(clientFetch === clientCount && clientFetched, clientError)}
        {t('client')}
        {t('loadingCount', { count: clientFetch, total: clientCount })}
      </div>
      <div style={orderDeliveriesError ? styles.infosError : styles.infos}>
        {pictoLoading(
          orderDeliveriesFetch === orderDeliveriesCount && orderDeliveriesFetched,
          orderDeliveriesError
        )}
        {t('loadingOrderDeliveries')}
        {t('loadingCount', { count: orderDeliveriesFetch, total: orderDeliveriesCount })}
      </div>
      <div style={salesError ? styles.infosError : styles.infos}>
        {pictoLoading(salesFetch === salesCount && salesFetched, salesError)}
        {t('sales')}
        {t('loadingCount', { count: salesFetch, total: salesCount })}
      </div>
      <div style={forwarderError ? styles.infosError : styles.infos}>
        {pictoLoading(forwarderFetch === forwarderCount && forwarderFetched, forwarderError)}
        {t('forwarder')}
        {t('loadingCount', { count: forwarderFetch, total: forwarderCount })}
      </div>
      <div style={addressesError ? styles.infosError : styles.infos}>
        {pictoLoading(addressesFetch === addressesCount && addressesFetched, addressesError)}
        {t('addresses')}
        {t('loadingCount', { count: addressesFetch, total: addressesCount })}
      </div>
      <div style={citiesError ? styles.infosError : styles.infos}>
        {pictoLoading(citiesFetch === citiesCount && citiesFetched, citiesError)}
        {t('cities')}
        {t('loadingCount', { count: citiesFetch, total: citiesCount })}
      </div>

      {(clientError ||
        salesError ||
        deliveryError ||
        userError ||
        catError ||
        addressesError ||
        citiesError) && (
        <>
          <p style={styles.error}>{t('loadingError')}</p>
          <div style={{ margin: '10px auto' }}>
            <Button text="Retourner à la page d'accueil" type="outline" onClick={gotoHomePage} />
          </div>
        </>
      )}
    </div>
  )
}

export default Loader
